import { dayjs, parseInTz } from "@/utils/date";
import type { Reservation } from "@/store/types";

/** A SEATED party that overlaps one of the BOOKED reservation's mesas,
 *  plus a flag telling the caller whether the SEATED's planned end falls
 *  past the BOOKED's start — i.e. the walk-in is *still going to be
 *  sitting* when the next party arrives. */
export interface WalkinOnMyMesa {
  other: Reservation;
  endLocal: dayjs.Dayjs;
  overlapsMyStart: boolean;
}

/** Pure: returns the worst (latest-ending) SEATED party currently
 *  occupying any of ``reservation``'s mesas, or ``null`` when there's
 *  none. Extracted from ``ReservationCard`` so the per-card cost can be
 *  pushed up to the list parent and the logic can be unit-tested
 *  without a render. */
export function computeWalkinOnMyMesa(
  reservation: Reservation,
  liveReservations: readonly Reservation[],
  now: dayjs.Dayjs,
  locationTz?: string,
): WalkinOnMyMesa | null {
  if (reservation.status !== "reservada" || reservation.tableIds.length === 0) {
    return null;
  }
  const myStart = parseInTz(
    `${reservation.date} ${reservation.time}`,
    "YYYY-MM-DD h:mm A",
    locationTz,
  );
  const tableIdSet = new Set(reservation.tableIds);
  const candidates = liveReservations
    .filter(
      (other) =>
        other.id !== reservation.id &&
        other.status === "sentado" &&
        other.tableIds.some((tid) => tableIdSet.has(tid)),
    )
    .map((other) => ({
      other,
      endLocal: parseInTz(
        `${other.endDate ?? other.date} ${other.endTime}`,
        "YYYY-MM-DD h:mm A",
        locationTz,
      ),
    }))
    // SEATED whose end has already passed shouldn't trigger the badge —
    // the auto-complete sweep should have moved them. If the sweep is
    // late they'll still appear as SEATED on the map but not as a
    // "walk-in hasta..." note here.
    .filter(({ endLocal }) => endLocal.isAfter(now))
    .toSorted((a, b) => b.endLocal.valueOf() - a.endLocal.valueOf());

  if (candidates.length === 0) return null;
  const newest = candidates[0];
  return { ...newest, overlapsMyStart: newest.endLocal.isAfter(myStart) };
}

/** Pre-compute the per-BOOKED-card walk-in overlap once for a whole
 *  list. Returns a Map keyed by reservation id so each ``ReservationCard``
 *  can receive its slice as a prop instead of subscribing to
 *  ``useLiveReservations`` independently. */
export function computeWalkinOnMyMesaMap(
  reservations: readonly Reservation[],
  liveReservations: readonly Reservation[],
  now: dayjs.Dayjs,
  locationTz?: string,
): Map<string, WalkinOnMyMesa> {
  const out = new Map<string, WalkinOnMyMesa>();
  for (const r of reservations) {
    const hit = computeWalkinOnMyMesa(r, liveReservations, now, locationTz);
    if (hit) out.set(r.id, hit);
  }
  return out;
}
